/**
 * Deterministic hashes: FNV-1a 32-bit over strings, bytes and quantized floats, plus canonical JSON
 * (combat §4.9 `fightHash(log)` = FNV-1a over canonical JSON; creatures §D1.12 pose hashes; architecture §5.10 palette
 * hashes quantized to 1e−5; save envelope checksum, architecture §4.1).
 */

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a 32-bit of a string's UTF-16 code units (u32). Continue a running hash with `seed`. */
export function fnv1a(str: string, seed: number = FNV_OFFSET): number {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

/** FNV-1a 32-bit over bytes (u32). */
export function fnv1aBytes(bytes: Uint8Array, seed: number = FNV_OFFSET): number {
  let h = seed >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i];
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

/** FNV-1a over numbers quantized to `quantum` (default 1e−5): each value → Math.round(v / quantum) as a decimal string. */
export function fnv1aFloats(values: ArrayLike<number>, quantum = 1e-5, seed: number = FNV_OFFSET): number {
  let h = seed >>> 0;
  for (let i = 0; i < values.length; i++) {
    const q = Math.round(values[i] / quantum);
    h = fnv1a(`${Object.is(q, -0) ? 0 : q};`, h);
  }
  return h >>> 0;
}

/** 8-digit lowercase hex of a u32. */
export const hex32 = (h: number): string => (h >>> 0).toString(16).padStart(8, '0');

/**
 * Canonical JSON: object keys sorted, `undefined` members dropped, non-finite numbers rejected. Two structurally equal
 * values always give the same string (the input of fight hashes and save checksums).
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError(`canonicalJson: non-finite number ${value}`);
    return JSON.stringify(Object.is(value, -0) ? 0 : value);
  }
  if (Array.isArray(value)) return `[${value.map((v) => (v === undefined ? 'null' : canonicalJson(v))).join(',')}]`;
  if (ArrayBuffer.isView(value)) return canonicalJson(Array.from(value as unknown as ArrayLike<number>));
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(',')}}`;
  }
  throw new TypeError(`canonicalJson: unsupported ${typeof value}`);
}

/** FNV-1a hex of the canonical JSON of a value. */
export const hashJson = (value: unknown): string => hex32(fnv1a(canonicalJson(value)));
